import {
  Box,
  Button,
  Heading,
  HStack,
  IconButton,
  Tag,
  Text,
  useColorModeValue,
  VStack,
} from '@chakra-ui/react';
import { Bot, Edit, Star, Zap } from 'lucide-react';
import React from 'react';
import { useLanguage } from '../../../hooks/utility/useLanguage';
import { Agent } from '../../../types/agentTypes';
import { getIconByName } from '../../../utils/iconUtils';
import { markdownToPlainText } from '../../../core/parsers/markdownParser';

interface AgentCardProps {
  agent: Agent;
  isSelected?: boolean;
  // Passed down from the page: calling useUser() per card would refetch the
  // (staleTime: 0) user query on every card mount.
  isSuperuser?: boolean;
  onSelect: (agent: Agent) => void;
  onEdit?: (agent: Agent) => void;
  onToggleFavorite?: (agentId: string) => void;
}

const AgentCard: React.FC<AgentCardProps> = ({
  agent,
  isSelected = false,
  isSuperuser = false,
  onSelect,
  onEdit,
  onToggleFavorite,
}) => {
  const { t } = useLanguage();
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const selectedBorderColor = useColorModeValue('primary', 'primary');
  const textColor = useColorModeValue('gray.800', 'white');
  const descriptionColor = useColorModeValue('gray.600', 'gray.300');
  const moreTagsColor = useColorModeValue('gray.500', 'gray.400');
  const iconColor = useColorModeValue('gray.600', 'white');

  const getAgentIcon = (agent: Agent) => {
    const IconComponent = getIconByName(agent.profile_picture_url);
    const FallbackIcon = agent.type === 'privileged' ? Zap : Bot;
    return IconComponent ? IconComponent : <FallbackIcon size={24} color={iconColor} />;
  };

  const handleFavoriteClick = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onToggleFavorite) {
      onToggleFavorite(agent.id);
    }
  };

  const handleEditClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onEdit) {
      onEdit(agent);
    }
  };

  const handleCardClick = () => {
    // Card click does nothing now - we use explicit buttons instead
  };

  const handleSelectClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onSelect(agent);
  };

  // Determine if edit button should be shown. Only superusers may edit, and only
  // normal (library) agents; privileged agents are managed via agents.yml/bootstrap.
  const showEditButton = !!onEdit && agent.type === 'normal' && isSuperuser

  // Check if agent has an icon
  const hasIcon = !!agent.profile_picture_url && !!getIconByName(agent.profile_picture_url);

  return (
    <Box
      id={`agent-card-${agent.id}`}
      bg={cardBg}
      borderWidth="1px"
      borderColor={isSelected ? selectedBorderColor : borderColor}
      borderRadius="lg"
      p={4}
      cursor="default" // Always default cursor - no clickable card behavior
      onClick={handleCardClick}
      transition="all 0.2s"
      boxShadow="sm"
      position="relative"
      minH="160px"
      display="flex"
      flexDirection="column"
    >
      {/* Action Buttons */}
      <HStack position="absolute" top={2} right={2} spacing={1} zIndex={2}>
        {/* Edit Button - Show for normal agents or for superusers on privileged agents */}
        {showEditButton && (
          <IconButton
            aria-label={t('library.editAgent')}
            icon={<Edit size={16} />}
            size="sm"
            variant="ghost"
            color="gray.500"
            _hover={{
              bg: 'primaryScheme.50',
              color: 'primary',
            }}
            onClick={handleEditClick}
          />
        )}

        {/* Favorite Button */}
        {onToggleFavorite && (
          <IconButton
            aria-label={agent.is_preferred ? t('library.removeFromFavorites') : t('library.addToFavorites')}
            icon={<Star size={16} fill={agent.is_preferred ? 'currentColor' : 'none'} />}
            size="sm"
            variant="ghost"
            color={agent.is_preferred ? 'yellow.500' : 'gray.400'}
            _hover={{
              color: agent.is_preferred ? 'yellow.600' : 'yellow.500',
            }}
            onClick={handleFavoriteClick}
          />
        )}
      </HStack>

      {/* Content Area - Flex grow to push button to bottom */}
      <Box flex="1">
        {/* Agent Icon and Header */}
        <HStack spacing={hasIcon ? 3 : 0} mb={2} align="start">
          {hasIcon && (
            <Box fontSize="24px" color="primary">
              {getAgentIcon(agent)}
            </Box>
          )}
          <VStack align="start" spacing={1} flex={1} minW={0}>
            <Heading size="sm" noOfLines={1} w="full" title={agent.name} color={textColor}>
              {agent.name.length > 27 ? agent.name.slice(0, 24) + '...' : agent.name}
            </Heading>
            {agent.type === 'privileged' && (
              <Tag size="sm" colorScheme="orange" variant="subtle">
                {t('library.privileged')}
              </Tag>
            )}
          </VStack>
        </HStack>

        {/* Description */}
        <Text fontSize="sm" mb={3} color={descriptionColor} noOfLines={3} pr={0}>
          {agent.description ? markdownToPlainText(agent.description) : t('library.noDescription')}
        </Text>

        {/* Tags */}
        {agent.tags && agent.tags.length > 0 && (
          <HStack spacing={2} mb={4} flexWrap="wrap">
            {agent.tags.slice(0, 3).map((tag, index) => (
              <Tag
                key={index}
                size="sm"
                variant="subtle"
                bg="brand.50"
                color="brand.primary"
                mt={1}
              >
                {tag.name}
              </Tag>
            ))}
            {agent.tags.length > 3 && (
              <Text fontSize="xs" color={moreTagsColor}>
                +{agent.tags.length - 3} {t('library.moreTags')}
              </Text>
            )}
          </HStack>
        )}
      </Box>

      {/* Select Button - Always at bottom */}
      <Button
        size="sm"
        variant="outline"
        colorScheme="primaryScheme"
        width="full"
        onClick={handleSelectClick}
        mt="auto"
      >
        {t('library.select')}
      </Button>
    </Box>
  );
};

export default AgentCard;
